import { useEffect, useRef, useState } from "react";
import { useAuth } from "react-oidc-context";
import { useQueryClient } from "@tanstack/react-query";

import Loader from "core/components/Loader";
import { isSessionExpiredError } from "common/apiError";
import { endSession } from "./endSession";
import {
  isRecentRedirect,
  readLastRedirectAt,
  recordRedirect,
} from "./redirectLoop";

/**
 * Sends the user back to Keycloak when the API rejects their access token.
 *
 * `HandleAuthError` covers the renewal that Keycloak refused, but that only
 * fires if the silent-renew timer runs. A laptop asleep past both token
 * lifetimes wakes with the timer overdue and the app already rendering, so the
 * first thing to learn the session is dead is an API call coming back 401.
 * Without this, those 401s were just query errors, and the first component to
 * see one decided the user had no account.
 */
const HandleSessionExpiry = ({ children }) => {
  const auth = useAuth();
  const queryClient = useQueryClient();
  const [hasExpired, setHasExpired] = useState(false);

  const previousRedirectAt = useRef(readLastRedirectAt()).current;
  const isLooping = isRecentRedirect(previousRedirectAt);

  useEffect(() => {
    const flagIfExpired = (event) => {
      if (isSessionExpiredError(event?.query?.state?.error)) {
        setHasExpired(true);
      }

      if (isSessionExpiredError(event?.mutation?.state?.error)) {
        setHasExpired(true);
      }
    };

    const unsubscribeQueries = queryClient
      .getQueryCache()
      .subscribe(flagIfExpired);
    const unsubscribeMutations = queryClient
      .getMutationCache()
      .subscribe(flagIfExpired);

    return () => {
      unsubscribeQueries();
      unsubscribeMutations();
    };
  }, [queryClient]);

  const hasRedirected = useRef(false);

  useEffect(() => {
    if (!hasExpired || isLooping || hasRedirected.current) return;

    hasRedirected.current = true;
    recordRedirect();
    void endSession({ auth, queryClient });
  }, [hasExpired, isLooping, auth, queryClient]);

  // Logging in didn't fix it, so the 401 is about this user's access rather
  // than their session. The error page beats redirecting forever.
  if (hasExpired && isLooping) {
    throw new Error("Session expired and logging in again did not restore it.");
  }

  if (hasExpired) {
    return <Loader style={{ height: "100dvh", paddingBottom: "20vh" }} />;
  }

  return children;
};

export default HandleSessionExpiry;
